declare global {
  interface Navigator {
    msSaveOrOpenBlob: (blob: Blob) => void;
  }
}

function getFileNameFromContentDispostionHeader(header?: string | null) {
  let fileName = "downloaded.pdf";

  if (typeof header === "undefined" || header === null) {
    return fileName;
  }

  const contentDispostion = header.split(";");
  const fileNameToken = "filename=";

  for (const thisValue of contentDispostion) {
    if (thisValue.trim().indexOf(fileNameToken) === 0) {
      fileName = decodeURIComponent(
        thisValue.trim().replace(fileNameToken, ""),
      );
      break;
    }
  }

  return fileName;
}

/**
 * Hand a downloaded response to the browser, throwing on any failure.
 *
 * Everything after the HTTP response can still fail: `response.blob()` can
 * reject, `createObjectURL` can throw, the anchor click can be blocked. A
 * caller that reports the download to the user needs to hear about those
 * rather than announce a file that never arrived (APP-1453).
 */
export async function processFileResponseOrThrow(response: Response) {
  const resObj = {
    filename: getFileNameFromContentDispostionHeader(
      response.headers.get("content-disposition"),
    ),
    blob: await response.blob(),
  };

  // It is necessary to create a new blob object with mime-type explicitly set for all browsers except Chrome, but it works for Chrome too.
  const newBlob = new Blob([resObj.blob], {
    type: response.headers.get("content-type") ?? "application/pdf",
  });

  // MS Edge and IE don't allow using a blob object directly as link href, instead it is necessary to use msSaveOrOpenBlob
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(newBlob);
  } else {
    // For other browsers: create a link pointing to the ObjectURL containing the blob.
    const objUrl = window.URL.createObjectURL(newBlob);

    const link = document.createElement("a");
    link.href = objUrl;
    link.download = resObj.filename;
    link.click();

    // For Firefox it is necessary to delay revoking the ObjectURL.
    setTimeout(() => {
      window.URL.revokeObjectURL(objUrl);
    }, 250);
  }
}

/**
 * Hand a downloaded response to the browser, logging any failure and
 * resolving regardless.
 *
 * This is the long-standing contract, kept unchanged for the callers that
 * have no error surface of their own. Prefer `processFileResponseOrThrow`
 * wherever the caller can report the failure to the user.
 */
export async function processFileResponse(response: Response) {
  try {
    await processFileResponseOrThrow(response);
  } catch (err) {
    console.error("DOWNLOAD ERROR", err);
  }
}

/**
 * `responseCallback` for a download query: hand the file to the browser, throwing
 * on any failure, and resolve `true`. React Query rejects a query result of
 * `undefined`, which `processFileResponseOrThrow` resolves with, so a download
 * query that reads its failure (`refetch({ throwOnError: true })`) needs this.
 */
export async function downloadFileResponse(response: Response) {
  await processFileResponseOrThrow(response);
  return true;
}
